/**
 * Masthead login control. Session-based login (see backend
 * app/api/routes_auth.py) is entirely independent of the manual API-key
 * entry still offered in App.tsx's Masthead -- an account can use either,
 * both, or switch between devices via login instead of copying a key
 * around, which is the whole point of this.
 */

import { useCallback, useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AuthResult, MeResult } from '../lib/types';
import { AccountDialog } from './AccountDialog';

type DialogState = 'login' | 'signup' | 'account' | null;

const SIGNED_OUT_ELSEWHERE_MESSAGE =
  'You were signed in on another device -- that device has been signed out.';

export function AuthMenu({ onAuthChange }: { onAuthChange: () => void | Promise<void> }) {
  const [me, setMe] = useState<MeResult | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  // Either "you were logged out because you're logged in elsewhere" (from
  // /me, seen on the OLD device next time it does anything) or "you just
  // signed another device out" (from a fresh login/signup response, seen
  // immediately on the NEW device) -- same banner, different source.
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.getMe();
      setMe(result);
      if (result.logged_out_message) setNotice(result.logged_out_message);
    } catch {
      setMe({
        authenticated: false,
        account_name: '',
        account_email: '',
        tier: 'free',
        subscription_status: '',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!me) return null;

  const afterAuthChange = async (result?: AuthResult) => {
    setDialog(null);
    if (result?.signed_out_other_device) setNotice(SIGNED_OUT_ELSEWHERE_MESSAGE);
    await refresh();
    await onAuthChange();
  };

  const noticeBanner = notice && (
    <div className="callout callout-warn" style={{ marginRight: '0.5rem' }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: '0.5rem' }}>
        <span>{notice}</span>
        <button type="button" className="btn-link" onClick={() => setNotice(null)}>
          Dismiss
        </button>
      </div>
    </div>
  );

  if (!me.authenticated) {
    return (
      <>
        {noticeBanner}
        <button type="button" className="btn btn-sm" onClick={() => setDialog('login')}>
          Log in
        </button>
        <button type="button" className="btn btn-sm btn-accent" onClick={() => setDialog('signup')}>
          Sign up
        </button>
        {dialog === 'login' && (
          <AuthDialog
            mode="login"
            onCancel={() => setDialog(null)}
            onSwitchMode={() => setDialog('signup')}
            onSuccess={afterAuthChange}
          />
        )}
        {dialog === 'signup' && (
          <AuthDialog
            mode="signup"
            onCancel={() => setDialog(null)}
            onSwitchMode={() => setDialog('login')}
            onSuccess={afterAuthChange}
          />
        )}
      </>
    );
  }

  return (
    <>
      {noticeBanner}
      <button type="button" className="btn btn-sm" onClick={() => setDialog('account')}>
        {me.account_name || 'My account'}
      </button>
      <button
        type="button"
        className="btn btn-sm"
        onClick={async () => {
          await api.logout();
          // A stored API key authenticates independently of the session
          // cookie (see api.deps.get_caller) -- clearing only the cookie
          // would leave this browser still "logged in" via the key the
          // moment the next request goes out. Logging out means logging
          // out of both.
          api.setApiKey('');
          await afterAuthChange();
        }}
      >
        Log out
      </button>
      {dialog === 'account' && (
        <AccountDialog onClose={() => setDialog(null)} onAccountChange={onAuthChange} />
      )}
    </>
  );
}

export function AuthDialog({
  mode,
  onCancel,
  onSwitchMode,
  onSuccess,
}: {
  mode: 'login' | 'signup';
  onCancel: () => void;
  onSwitchMode: () => void;
  onSuccess: (result: AuthResult) => void | Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result =
        mode === 'login'
          ? await api.login({ email, password })
          : await api.signup({ email, password, name: name || undefined });
      await onSuccess(result);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : mode === 'login'
            ? 'Could not log in.'
            : 'Could not sign up.',
      );
      setBusy(false);
    }
  }

  async function sendForgotPassword() {
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email);
      setForgotSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send reset email.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'login' ? 'Log in' : 'Sign up'}
    >
      <div className="card">
        <div className="card-head">
          <h2>{mode === 'login' ? 'Log in' : 'Create your account'}</h2>
        </div>
        <div className="card-body stack">
          {forgotSent ? (
            <>
              <p className="hint">
                If an account exists for <strong>{email}</strong>, a password reset link has been
                sent.
              </p>
              <button type="button" className="btn btn-sm" onClick={() => setForgotSent(false)}>
                Back
              </button>
            </>
          ) : (
            <>
              {mode === 'signup' && (
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  aria-label="Name"
                />
              )}
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoFocus
                aria-label="Email address"
              />
              <input
                type="password"
                placeholder={mode === 'signup' ? 'Password (min. 10 characters)' : 'Password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-label="Password"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !busy && email && password) void submit();
                }}
              />

              {error && (
                <div className="hint" style={{ color: 'var(--fail-600, #c0392b)' }}>
                  {error}
                </div>
              )}

              {mode === 'login' && (
                <button
                  type="button"
                  className="btn-link"
                  disabled={busy || !email}
                  onClick={sendForgotPassword}
                  style={{ textAlign: 'left' }}
                >
                  Forgot your password?
                </button>
              )}

              <div className="row" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="btn-link" onClick={onSwitchMode} disabled={busy}>
                  {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
                </button>
              </div>

              <div className="row" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-sm" onClick={onCancel} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  disabled={busy || !email || !password}
                  onClick={submit}
                >
                  {busy ? 'Please wait…' : mode === 'login' ? 'Log in' : 'Sign up'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
